import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { AccountHeader } from '@mobi/component-library/Account'
import { Button, InfoBox, Input } from '@mobi/component-library/Common/V2'
import { DropdownInput } from '@mobi/component-library/Selection&Input'
import { DocumentPermission } from '@mobi/account/Areas/SignUp/Components/Verification'
import { DocumentBodyStyled, FormLayout } from './Layout.styled'
import {
  type TypeSignUpApplicationResponse,
  useCountryList,
  useVerifyPassport,
  type TypePassportDetailsRequest,
} from '../../Hooks'
import {
  ALPHANUMERIC_REGEX,
  getNameValidationMessage,
  getRequiredValidationMessage,
  isValidName,
  isVerificationSuccessful,
  routes,
} from '@mobi/account/Areas/SignUp/Utils'
import {
  getSignUpState,
  setNumberOfAttemptsToVerify,
  setSignupApplication,
  setSignupVerification,
} from '../../Store'
import { SignupError } from '../SignupError'
import { Contract } from '@mobi/web-native-comms'
import { ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT } from '../../Utils'
import {
  trackVerificationEnterIdentification,
  trackVerificationSuccess,
} from '@mobi/account/Areas/Verification/Utils/ga'
import { trackSignUpError } from '../../Utils/ga'
import { unwrapErrorMessage } from '@mobi/utils/errors'
import { NameInput } from '@mobi/account/Common'
import {
  PopularCountryList,
  AlphabeticalCountryList,
} from '@mobi/account/Common/CountryOptionGroups/CountryOptionGroups'

interface PassportProps {
  trackOptimoveEvent: (event: Contract['OPTIMOVE_TRACK']) => void
}

export const Passport = ({ trackOptimoveEvent }: PassportProps) => {
  const [passportNumber, setPassportNumber] = React.useState('')
  const [passportCountry, setPassportCountry] = React.useState<string | null>('AUS')
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const signupApplication = useSelector(getSignUpState)?.signupApplication
  const countryList = useCountryList()
  const { verifyPassport, verifyPassportStatus } = useVerifyPassport()

  const [firstName, setFirstName] = React.useState('')
  const [middleName, setMiddleName] = React.useState('')
  const [surname, setSurname] = React.useState('')

  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const allowToProceed =
    hasValidEntries &&
    givePermission &&
    !!passportNumber &&
    !!passportCountry &&
    isValidName(passportCountry) &&
    isValidName(firstName) &&
    isValidName(middleName, true) &&
    isValidName(surname)
  const isBusy = countryList.isLoading || verifyPassportStatus === 'pending'

  const onSubmit = () => {
    if (
      !signupApplication ||
      !signupApplication.applicationReferenceNumber ||
      !signupApplication.enquiryId ||
      !allowToProceed
    ) {
      return
    }
    const passport: TypePassportDetailsRequest = {
      birthdate: signupApplication.birthdate,
      firstName: firstName,
      surname: surname,
      middleName: middleName,
      email: signupApplication.email,
      mobileNumber: signupApplication.mobileNumber,
      residentialAddress: signupApplication.residentialAddress,
      previousAddress: signupApplication.previousAddress,
      enquiryId: signupApplication.enquiryId,
      applicationReferenceNumber: signupApplication.applicationReferenceNumber,
      passportDetails: {
        number: passportNumber,
        passportCountry: passportCountry,
      },
      marketingOptInIndicator: signupApplication.marketingOptInIndicator,
    }
    dispatch(setSignupApplication({ ...signupApplication, firstName, middleName, surname }))
    verifyPassport(passport, {
      onSuccess: verification => {
        if (signupApplication.applicationReferenceNumber != null) {
          const verificationResponse: TypeSignUpApplicationResponse = {
            applicationReferenceNumber: signupApplication.applicationReferenceNumber,
            enquiryId: verification.enquiryId,
            duplicateExists: false,
            isDuplicateClosed: false,
            isVerified: verification.isVerified,
            unVerifiedReason: verification.unVerifiedReason,
            potentialFraudMatch: verification.potentialFraudMatch,
            potentialSanctionedMatch: verification.potentialSanctionedMatch,
          }
          dispatch(setSignupVerification(verificationResponse))
          const isSuccessful = isVerificationSuccessful(verificationResponse)

          trackVerificationEnterIdentification('passport', isSuccessful)
          if (isSuccessful) {
            trackVerificationSuccess('Verified Identification')
            trackOptimoveEvent(ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT)
            navigate(routes.password)
            return
          }

          dispatch(setNumberOfAttemptsToVerify())
          if (
            verificationResponse.duplicateExists ||
            verificationResponse.potentialSanctionedMatch ||
            !verificationResponse.isVerified
          ) {
            navigate(routes.signUpUnsuccessful)
            return
          }
        }
      },
      onError: error => {
        trackSignUpError(location.pathname, unwrapErrorMessage(error))
        setShowErrorPopup(true)
      },
    })
  }

  if (!signupApplication) {
    return null
  }

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  return (
    <>
      <FormLayout>
        <AccountHeader
          alignItems='start'
          title='Enter your Passport details'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your details as they appear on your ID document.
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <DocumentBodyStyled>
          <NameInput
            firstName={firstName}
            middleName={middleName}
            lastName={surname}
            setFirstName={setFirstName}
            setMiddleName={setMiddleName}
            setLastName={setSurname}
            isDisabled={isBusy}
          />
          <Input
            name='passportNumber'
            label='Passport number'
            maxLength={15}
            placeholder='Enter passport number'
            isDisabled={isBusy}
            value={passportNumber}
            onChange={e => {
              const filteredText = e.target.value?.replace(ALPHANUMERIC_REGEX, '')
              setPassportNumber(filteredText)
            }}
            onFocus={() => resetFieldValidation('passportNumber')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                passportNumber: getRequiredValidationMessage(passportNumber),
              }))
            }}
            isInvalid={!!validationMessages.passportNumber}
            helpText={validationMessages.passportNumber}
          />
          <DropdownInput
            name='passportCountry'
            label='Issuing country'
            placeholder='Select country'
            value={passportCountry || ''}
            isDisabled={isBusy}
            onChange={event => {
              setPassportCountry(event.target.value || '')
            }}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                passportCountry: getNameValidationMessage(passportCountry),
              }))
            }}
            isInvalid={!!validationMessages.passportCountry}
            helpText={validationMessages.passportCountry}
          >
            <PopularCountryList countries={countryList.data ?? []} />
            <AlphabeticalCountryList countries={countryList.data ?? []} />
          </DropdownInput>
          <DocumentPermission isBusy={isBusy} onChange={e => setGivePermission(e.target.checked)} />
        </DocumentBodyStyled>

        <footer>
          <Button isFullWidth disabled={!allowToProceed} isLoading={isBusy} onClick={onSubmit}>
            Continue
          </Button>
        </footer>
      </FormLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}
